import type { ISerialize } from '@/objects/ISerialize';
import { RaceContestantDetails } from './RaceContestantDetails';
import { EventinfoBase } from './EventinfoBase';
import { EventSetup } from './EventSetup';
import { ImportFormat } from './ImportFormat';
import type { BibHandleType } from './../enums/BibHandleType';

export class UpdateEventRequest implements ISerialize {
    public Contestants: RaceContestantDetails[];

    public EventInfo: EventinfoBase | null;

    public EventSetup: EventSetup | null;

    public ImportFormat: ImportFormat | null;

    public SignalrKey: string | null;

    public SourceEventBibHandleType: BibHandleType;

    /***
     * @deprecated Use the static FromJsValues method instead of the constructor to get type safety and default values
     */
    public constructor(values: {
        Contestants?: RaceContestantDetails[];
        EventInfo?: EventinfoBase | null;
        EventSetup?: EventSetup | null;
        ImportFormat?: ImportFormat | null;
        SignalrKey?: string | null;
        SourceEventBibHandleType?: BibHandleType;
    }) {
        this.Contestants = values.Contestants ?? [];

        this.EventInfo = values.EventInfo ?? null;

        this.EventSetup = values.EventSetup ?? null;

        this.ImportFormat = values.ImportFormat ?? null;

        this.SignalrKey = values.SignalrKey ?? null;

        this.SourceEventBibHandleType = values.SourceEventBibHandleType ?? 'None';
    }

    public serialize(): string {
        return (
            '{' +
            `"Contestants": [ ${this.Contestants.map((x) => (!x ? null : x?.serialize()))} ]` +
            ',' +
            `"EventInfo": ${!this.EventInfo ? null : this.EventInfo?.serialize()}` +
            ',' +
            `"EventSetup": ${!this.EventSetup ? null : this.EventSetup?.serialize()}` +
            ',' +
            `"ImportFormat": ${!this.ImportFormat ? null : this.ImportFormat?.serialize()}` +
            ',' +
            `"SignalrKey": ${JSON.stringify(this.SignalrKey)}` +
            ',' +
            `"SourceEventBibHandleType": ${this.SourceEventBibHandleType == 'None' ? 0 : this.SourceEventBibHandleType == 'MoveAll' ? 1 : this.SourceEventBibHandleType == 'KeepChip' ? 2 : this.SourceEventBibHandleType == 'KeepDevice' ? 3 : this.SourceEventBibHandleType == 'KeepAll' ? 4 : 0}` +
            '}'
        );
    }

    /**
     * Creates an instance of UpdateEventRequest from the supplied values.
     * Missing values have default values
     * @param values The values you want to set
     * @returns An object instance with the supplied values set
     */
    public static FromJsValues(values: {
        Contestants?: RaceContestantDetails[];
        EventInfo?: EventinfoBase | null;
        EventSetup?: EventSetup | null;
        ImportFormat?: ImportFormat | null;
        SignalrKey?: string | null;
        SourceEventBibHandleType?: BibHandleType;
    }): UpdateEventRequest {
        return new UpdateEventRequest(values);
    }

    public static _FromServerJson(json: any): UpdateEventRequest {
        return new UpdateEventRequest({
            Contestants: json.Contestants?.map((x: any) => RaceContestantDetails._FromServerJson(x)),
            EventInfo: json.EventInfo ? EventinfoBase._FromServerJson(json.EventInfo) : null,
            EventSetup: json.EventSetup ? EventSetup._FromServerJson(json.EventSetup) : null,
            ImportFormat: json.ImportFormat ? ImportFormat._FromServerJson(json.ImportFormat) : null,
            SignalrKey: json.SignalrKey ?? '',
            SourceEventBibHandleType:
                json.SourceEventBibHandleType == 0
                    ? 'None'
                    : json.SourceEventBibHandleType == 1
                      ? 'MoveAll'
                      : json.SourceEventBibHandleType == 2
                        ? 'KeepChip'
                        : json.SourceEventBibHandleType == 3
                          ? 'KeepDevice'
                          : json.SourceEventBibHandleType == 4
                            ? 'KeepAll'
                            : 'None',
        });
    }

    /**
     * Creates an instance of UpdateEventRequest from another instance of UpdateEventRequest.
     * @param other The UpdateEventRequest you want to clone
     * @returns A cloned UpdateEventRequest
     */
    public static FromOther(other: UpdateEventRequest): UpdateEventRequest {
        return UpdateEventRequest._FromServerJson(JSON.parse(other.serialize()));
    }

    /**
     * Deepclones the object.
     */
    public DeepClone() {
        return UpdateEventRequest.FromOther(this);
    }

    static NAME_OF_CLASS = 'UpdateEventRequest';
}
